import React, { createContext, useContext, useState, useEffect } from 'react';

const BUILTIN_THEMES = {
  signature: {
    id: 'signature',
    name: 'Signature',
    isCustom: false,
    // Match landing page: deep #0a0118 solid bg + ambient purple/cyan glow orbs
    bg: 'bg-[#0a0118]',
    cardBg: 'bg-white/[0.03] backdrop-blur-md border-white/[0.06]',
    cardAccentBorder: 'ring-purple-500/25',
    headerGradient: 'from-purple-400 via-cyan-400 to-purple-400',
    subtitle: 'text-white/50',
    accentBtn: 'bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700',
    badgeBtn: 'bg-white/[0.04] border-purple-500/20 hover:bg-white/[0.08]',
    statsBtn: 'bg-white/[0.04] border-cyan-500/20 hover:bg-white/[0.08]',
    presetHeader: 'bg-gradient-to-r from-purple-600/15 to-cyan-600/15',
    toggleBg: 'bg-white/[0.04] border-white/10 hover:bg-white/[0.08]',
    inputBg: 'bg-white/[0.04] border-white/10 text-white',
    selectBg: 'bg-[#13081f] border-white/10',
    infoBg: 'bg-white/[0.03] border-white/[0.06]',
    tagBg: 'bg-purple-500/15',
    mutedText: 'text-white/55',
    dimText: 'text-white/35',
    primaryText: 'text-white',
    icon: '✦',
    bgStyle: null,
    accentColor: null,
  },
  cosmic: {
    id: 'cosmic',
    name: 'Cosmic',
    isCustom: false,
    bg: 'bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900',
    cardBg: 'bg-white/10 backdrop-blur-md border-white/20',
    cardAccentBorder: 'ring-purple-500/30',
    headerGradient: 'from-purple-400 to-cyan-400',
    subtitle: 'text-purple-200',
    accentBtn: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700',
    badgeBtn: 'bg-gradient-to-r from-yellow-600/20 to-amber-600/20 border-yellow-500/30',
    statsBtn: 'bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-purple-500/30',
    presetHeader: 'bg-gradient-to-r from-purple-600/20 to-blue-600/20',
    toggleBg: 'bg-white/10 border-white/20 hover:bg-white/20',
    inputBg: 'bg-white/10 border-white/20 text-white',
    selectBg: 'bg-gray-800 border-white/20',
    infoBg: 'bg-white/5 border-white/20',
    tagBg: 'bg-white/20',
    mutedText: 'text-white/70',
    dimText: 'text-white/60',
    primaryText: 'text-white',
    icon: '🌌',
    bgStyle: null,
    accentColor: null,
  },
  ocean: {
    id: 'ocean',
    name: 'Midnight Ocean',
    isCustom: false,
    bg: 'bg-gradient-to-br from-slate-950 via-teal-950 to-cyan-950',
    cardBg: 'bg-white/10 backdrop-blur-md border-teal-500/20',
    cardAccentBorder: 'ring-teal-500/30',
    headerGradient: 'from-teal-400 to-emerald-300',
    subtitle: 'text-teal-200',
    accentBtn: 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700',
    badgeBtn: 'bg-gradient-to-r from-yellow-600/20 to-amber-600/20 border-yellow-500/30',
    statsBtn: 'bg-gradient-to-r from-teal-600/20 to-cyan-600/20 border-teal-500/30',
    presetHeader: 'bg-gradient-to-r from-teal-600/20 to-cyan-600/20',
    toggleBg: 'bg-white/10 border-teal-500/30 hover:bg-white/20',
    inputBg: 'bg-white/10 border-teal-500/20 text-white',
    selectBg: 'bg-slate-900 border-teal-500/20',
    infoBg: 'bg-teal-500/5 border-teal-500/20',
    tagBg: 'bg-teal-500/20',
    mutedText: 'text-teal-100/70',
    dimText: 'text-teal-100/50',
    primaryText: 'text-white',
    icon: '🌊',
    bgStyle: null,
    accentColor: null,
  },
  ember: {
    id: 'ember',
    name: 'Ember',
    isCustom: false,
    bg: 'bg-gradient-to-br from-gray-950 via-stone-900 to-neutral-900',
    cardBg: 'bg-white/10 backdrop-blur-md border-amber-500/20',
    cardAccentBorder: 'ring-amber-500/30',
    headerGradient: 'from-amber-400 to-orange-300',
    subtitle: 'text-amber-200',
    accentBtn: 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700',
    badgeBtn: 'bg-gradient-to-r from-yellow-600/20 to-amber-600/20 border-yellow-500/30',
    statsBtn: 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-500/30',
    presetHeader: 'bg-gradient-to-r from-amber-600/20 to-orange-600/20',
    toggleBg: 'bg-white/10 border-amber-500/30 hover:bg-white/20',
    inputBg: 'bg-white/10 border-amber-500/20 text-white',
    selectBg: 'bg-gray-900 border-amber-500/20',
    infoBg: 'bg-amber-500/5 border-amber-500/20',
    tagBg: 'bg-amber-500/20',
    mutedText: 'text-amber-100/70',
    dimText: 'text-amber-100/50',
    primaryText: 'text-white',
    icon: '🔥',
    bgStyle: null,
    accentColor: null,
  },
};

const BUILTIN_ORDER = ['signature', 'cosmic', 'ocean', 'ember'];

// Helper: lighten a hex color
function lightenHex(hex, amount = 0.3) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, ((num >> 16) & 0xFF) + Math.round(255 * amount));
  const g = Math.min(255, ((num >> 8) & 0xFF) + Math.round(255 * amount));
  const b = Math.min(255, (num & 0xFF) + Math.round(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// Generate a full theme object from custom color inputs
export function buildCustomTheme({ id, name, icon, gradientStart, gradientMid, gradientEnd, accent }) {
  const accentLight = lightenHex(accent, 0.25);
  return {
    id,
    name,
    isCustom: true,
    icon: icon || '🎨',
    // Custom themes use inline styles for background — bg class is empty
    bg: '',
    bgStyle: { background: `linear-gradient(to bottom right, ${gradientStart}, ${gradientMid}, ${gradientEnd})` },
    accentColor: accent,
    cardBg: 'bg-white/10 backdrop-blur-md',
    cardAccentBorder: '',
    headerGradient: '',
    subtitle: 'text-white/70',
    accentBtn: '',
    badgeBtn: 'bg-gradient-to-r from-yellow-600/20 to-amber-600/20 border-yellow-500/30',
    statsBtn: '',
    presetHeader: '',
    toggleBg: 'bg-white/10 hover:bg-white/20',
    inputBg: 'bg-white/10 text-white',
    selectBg: 'bg-gray-900',
    infoBg: 'bg-white/5',
    tagBg: 'bg-white/20',
    mutedText: 'text-white/70',
    dimText: 'text-white/60',
    primaryText: 'text-white',
    // Inline style overrides for accent-dependent properties
    cardBorderStyle: { borderColor: `${accent}33` },
    cardAccentStyle: { boxShadow: `0 0 0 2px ${accent}4D` },
    headerGradientStyle: { backgroundImage: `linear-gradient(to right, ${accentLight}, ${accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    subtitleStyle: { color: `${accentLight}CC` },
    accentBtnStyle: { background: `linear-gradient(to right, ${accent}, ${accentLight})` },
    statsBtnStyle: { background: `${accent}33`, borderColor: `${accent}4D` },
    presetHeaderStyle: { background: `linear-gradient(to right, ${accent}33, ${accentLight}33)` },
    toggleBorderStyle: { borderColor: `${accent}4D` },
    inputBorderStyle: { borderColor: `${accent}33` },
    selectBgStyle: { borderColor: `${accent}33` },
    infoBgStyle: { background: `${accent}0D`, borderColor: `${accent}33` },
    tagBgStyle: { background: `${accent}33` },
    // Store raw colors for editing
    colors: { gradientStart, gradientMid, gradientEnd, accent },
  };
}

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState(() => {
    // One-time migration (Feb 2026): bump users still on the previous default
    // ('cosmic', auto-saved by older app versions) to the new 'signature' theme
    // that matches the landing page aesthetic. Users who actively picked
    // 'ocean', 'ember', or a custom theme are left untouched.
    try {
      const MIGRATION_KEY = 'neurowave_theme_migrated_to_signature_v1';
      if (!localStorage.getItem(MIGRATION_KEY)) {
        const current = localStorage.getItem('neurowave_theme');
        if (!current || current === 'cosmic') {
          localStorage.setItem('neurowave_theme', 'signature');
        }
        localStorage.setItem(MIGRATION_KEY, '1');
      }
      return localStorage.getItem('neurowave_theme') || 'signature';
    } catch (e) {
      // localStorage blocked/unavailable — fall back to the default theme
      return 'signature';
    }
  });

  const [customThemes, setCustomThemes] = useState(() => {
    try {
      const saved = localStorage.getItem('neurowave_custom_themes');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Rebuild theme objects from stored colors
        return parsed.map(ct => buildCustomTheme(ct));
      }
    } catch (e) { /* ignore */ }
    return [];
  });

  // All themes combined
  const allThemes = { ...BUILTIN_THEMES };
  customThemes.forEach(ct => { allThemes[ct.id] = ct; });

  const themeOrder = [...BUILTIN_ORDER, ...customThemes.map(ct => ct.id)];
  const theme = allThemes[themeId] || BUILTIN_THEMES.signature;

  useEffect(() => {
    try { localStorage.setItem('neurowave_theme', themeId); } catch (e) { /* ignore */ }
  }, [themeId]);

  useEffect(() => {
    const toStore = customThemes.map(ct => ({
      id: ct.id,
      name: ct.name,
      icon: ct.icon,
      ...ct.colors,
    }));
    try { localStorage.setItem('neurowave_custom_themes', JSON.stringify(toStore)); } catch (e) { /* ignore */ }
  }, [customThemes]);

  const cycleTheme = () => {
    const idx = themeOrder.indexOf(themeId);
    const next = themeOrder[(idx + 1) % themeOrder.length];
    setThemeId(next);
  };

  const addCustomTheme = (themeData) => {
    const newTheme = buildCustomTheme(themeData);
    setCustomThemes(prev => [...prev, newTheme]);
    setThemeId(newTheme.id);
  };

  const updateCustomTheme = (themeData) => {
    const updated = buildCustomTheme(themeData);
    setCustomThemes(prev => prev.map(ct => ct.id === updated.id ? updated : ct));
  };

  const deleteCustomTheme = (id) => {
    setCustomThemes(prev => prev.filter(ct => ct.id !== id));
    if (themeId === id) setThemeId('signature');
  };

  return (
    <ThemeContext.Provider value={{
      theme, themeId, cycleTheme, setThemeId,
      allThemes, themeOrder, customThemes,
      addCustomTheme, updateCustomTheme, deleteCustomTheme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
